import { Injectable, signal } from '@angular/core';
import {
  AiHelpDataContent,
  AiHelpGuideContent,
  AiHelpMessageContent
} from '../models/ai-help-assistant.models';
import {
  formatDataLabel,
  formatHours,
  formatDataBlockStatValue,
  formatItemListOverProjectLine,
  formatItemListItemLine,
  formatItemListTicketLine,
  formatKeyValueLabel,
  formatRankingEntryValue,
  formatDailyHoursDate,
  formatPercentComplete,
  formatDaysOverdue,
  formatOverdueCount,
  formatDaysRemaining,
  formatRecordFieldValue,
  isCompleteProjectHealth,
  isDisplayableDataKey,
  resolveBlocksItemLabel
} from '@app/features/utils/ai-help-response.parser';

export interface AiHelpSpeechPart {
  text: string;
  methodIndex?: number;
  stepIndex?: number;
  fieldKey?: string;
  partKind: 'intro' | 'method-label' | 'step' | 'note' | 'data-field' | 'link-cue';
}

@Injectable({ providedIn: 'root' })
export class AiHelpSpeechSynthesisService {
  private readonly _isSupported = signal(this.detectSupport());
  private readonly _isSpeaking = signal(false);
  private readonly _activeMessageId = signal<string | null>(null);
  private readonly _activePart = signal<AiHelpSpeechPart | null>(null);

  readonly isSupported = this._isSupported.asReadonly();
  readonly isSpeaking = this._isSpeaking.asReadonly();
  readonly activeMessageId = this._activeMessageId.asReadonly();
  readonly activePart = this._activePart.asReadonly();

  private speakSessionId = 0;

  speakMessage(messageId: string, content: AiHelpMessageContent, onComplete?: () => void): void {
    if (!this._isSupported()) {
      onComplete?.();
      return;
    }

    if (content.kind === 'guide') {
      const parts = hasExpandableAlternatives(content)
        ? buildExpandableGuideOverviewParts(content)
        : buildGuideSpeechParts(content);
      this.speakParts(messageId, parts, onComplete);
      return;
    }

    if (content.kind === 'data') {
      this.speakParts(messageId, buildDataSpeechParts(content), onComplete);
      return;
    }

    if (content.text.trim()) {
      this.speakParts(messageId, [{ text: content.text.trim(), partKind: 'intro' }], onComplete);
      return;
    }

    onComplete?.();
  }

  speakGuideMethod(
    messageId: string,
    content: AiHelpGuideContent,
    methodIndex: number,
    onComplete?: () => void
  ): void {
    if (!this._isSupported()) {
      onComplete?.();
      return;
    }

    const parts = buildGuideMethodSpeechParts(content, methodIndex);
    if (parts.length === 0) {
      onComplete?.();
      return;
    }

    this.speakParts(messageId, parts, onComplete);
  }

  stop(): void {
    this.speakSessionId += 1;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    this._isSpeaking.set(false);
    this._activeMessageId.set(null);
    this._activePart.set(null);
  }

  isSpeakingMessage(messageId: string): boolean {
    return this._isSpeaking() && this._activeMessageId() === messageId;
  }

  isSpeakingStep(messageId: string, methodIndex: number, stepIndex: number): boolean {
    if (!this.isSpeakingMessage(messageId)) {
      return false;
    }
    const part = this._activePart();
    return (
      part?.partKind === 'step' &&
      part.methodIndex === methodIndex &&
      part.stepIndex === stepIndex
    );
  }

  isSpeakingMethod(messageId: string, methodIndex: number): boolean {
    if (!this.isSpeakingMessage(messageId)) {
      return false;
    }
    const part = this._activePart();
    return (
      part != null &&
      (part.partKind === 'method-label' || part.partKind === 'step') &&
      part.methodIndex === methodIndex
    );
  }

  private speakParts(messageId: string, parts: AiHelpSpeechPart[], onComplete?: () => void): void {
    if (parts.length === 0 || typeof window === 'undefined' || !window.speechSynthesis) {
      onComplete?.();
      return;
    }

    this.stop();
    const sessionId = this.speakSessionId;
    this._isSpeaking.set(true);
    this._activeMessageId.set(messageId);

    let index = 0;

    const speakNext = (): void => {
      if (sessionId !== this.speakSessionId) {
        return;
      }

      if (index >= parts.length) {
        this.finishSpeaking();
        onComplete?.();
        return;
      }

      const part = parts[index];
      this._activePart.set(part);

      const utterance = new SpeechSynthesisUtterance(part.text);
      utterance.lang = this.resolveLanguage();
      utterance.rate = 1;

      utterance.onend = () => {
        index += 1;
        window.setTimeout(() => speakNext(), 80);
      };

      utterance.onerror = () => {
        index += 1;
        window.setTimeout(() => speakNext(), 80);
      };

      window.speechSynthesis.speak(utterance);
      this.ensureSpeaking();
    };

    speakNext();
  }

  private finishSpeaking(): void {
    this._isSpeaking.set(false);
    this._activeMessageId.set(null);
    this._activePart.set(null);
  }

  private ensureSpeaking(): void {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      return;
    }

    const resume = (): void => {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
    };

    resume();
    window.setTimeout(resume, 100);
  }

  private detectSupport(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  private resolveLanguage(): string {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return 'en-US';
  }
}

export function isExpandableGuideMethod(method: { alternative?: string | null }): boolean {
  return !!method.alternative?.trim();
}

export function hasExpandableAlternatives(content: AiHelpGuideContent): boolean {
  return content.methods.some((method) => isExpandableGuideMethod(method));
}

export function buildExpandableGuideOverviewParts(content: AiHelpGuideContent): AiHelpSpeechPart[] {
  const parts: AiHelpSpeechPart[] = [];

  if (content.intro.trim()) {
    parts.push({ text: content.intro.trim(), partKind: 'intro' });
  }

  const alternatives = content.methods
    .map((method, methodIndex) => ({ method, methodIndex }))
    .filter(({ method }) => isExpandableGuideMethod(method) && !!method.label?.trim());

  if (alternatives.length > 1) {
    parts.push({
      text: `You have ${alternatives.length} ways to do this.`,
      partKind: 'intro'
    });
  }

  alternatives.forEach(({ method, methodIndex }) => {
    parts.push({
      text: method.label!.trim(),
      methodIndex,
      partKind: 'method-label'
    });
  });

  if (alternatives.length > 0) {
    parts.push({
      text: 'Select an option to hear the steps.',
      partKind: 'intro'
    });
  }

  return parts;
}

export function buildGuideMethodSpeechParts(
  content: AiHelpGuideContent,
  methodIndex: number,
  includeNote = true
): AiHelpSpeechPart[] {
  const method = content.methods[methodIndex];
  if (!method) {
    return [];
  }

  const parts: AiHelpSpeechPart[] = [];

  method.steps.forEach((step, stepIndex) => {
    const stepText = step.text.trim().replace(/[.!?]+$/, '');
    parts.push({
      text: `Step ${stepIndex + 1}. ${stepText}.`,
      methodIndex,
      stepIndex,
      partKind: 'step'
    });
  });

  // Speak step text as shown; only after all steps, cue the hyperlink (no label repeat).
  if (method.steps.some((step) => !!step.link)) {
    parts.push({
      text: 'Click the existing hyperlink.',
      methodIndex,
      partKind: 'link-cue'
    });
  }

  if (includeNote && content.note?.trim()) {
    parts.push({ text: content.note.trim(), partKind: 'note' });
  }

  return parts;
}

export function buildGuideSpeechParts(content: AiHelpGuideContent): AiHelpSpeechPart[] {
  const parts: AiHelpSpeechPart[] = [];

  if (content.intro.trim()) {
    parts.push({ text: content.intro.trim(), partKind: 'intro' });
  }

  content.methods.forEach((method, methodIndex) => {
    if (method.label?.trim()) {
      parts.push({
        text: method.label.trim(),
        methodIndex,
        partKind: 'method-label'
      });
    }

    parts.push(...buildGuideMethodSpeechParts(content, methodIndex, false));
  });

  if (content.note?.trim()) {
    parts.push({ text: content.note.trim(), partKind: 'note' });
  }

  return parts;
}

export function buildDataSpeechParts(content: AiHelpDataContent): AiHelpSpeechPart[] {
  const parts: AiHelpSpeechPart[] = [];

  if (content.intro.trim()) {
    parts.push({ text: content.intro.trim(), partKind: 'intro' });
  }

  const preferProjectList =
    content.dataKind === 'project_list' && (content.projects?.length ?? 0) > 0;

  if (!preferProjectList && content.blocks?.length) {
    const blockItemLabel = resolveBlocksItemLabel(content.blocks);
    content.blocks.forEach((block, blockIdx) => {
      if (block.type === 'stat_cards') {
        if (block.title?.trim()) {
          parts.push({
            text: block.title.trim(),
            fieldKey: `block-${blockIdx}-title`,
            partKind: 'data-field'
          });
        }
        block.items.forEach((item, itemIdx) => {
          parts.push({
            text: `${item.label}: ${formatDataBlockStatValue(item.value)}`,
            fieldKey: `block-${blockIdx}-stat-${itemIdx}`,
            partKind: 'data-field'
          });
        });
        return;
      }

      if (block.type === 'item_list') {
        if (block.title?.trim()) {
          parts.push({
            text: block.title.trim(),
            fieldKey: `block-${blockIdx}-title`,
            partKind: 'data-field'
          });
        }
        block.items.forEach((item, itemIdx) => {
          parts.push({
            text: formatItemListItemLine(item),
            fieldKey: `block-${blockIdx}-item-${itemIdx}`,
            partKind: 'data-field'
          });
          item.tickets?.forEach((ticket, ticketIdx) => {
            parts.push({
              text: formatItemListTicketLine(ticket),
              fieldKey: `block-${blockIdx}-item-${itemIdx}-ticket-${ticketIdx}`,
              partKind: 'data-field'
            });
          });
          item.overProjects?.forEach((project, projectIdx) => {
            parts.push({
              text: formatItemListOverProjectLine(project),
              fieldKey: `block-${blockIdx}-item-${itemIdx}-project-${projectIdx}`,
              partKind: 'data-field'
            });
          });
        });
        return;
      }

      if (block.type === 'ranking') {
        if (block.title?.trim()) {
          parts.push({
            text: block.title.trim(),
            fieldKey: `block-${blockIdx}-title`,
            partKind: 'data-field'
          });
        }
        block.items.forEach((entry, itemIdx) => {
          const value = formatRankingEntryValue(entry, blockItemLabel);
          const share = entry.percent != null ? `, ${formatPercentComplete(entry.percent)}` : '';
          const typeSuffix = entry.type ? ` (${entry.type})` : '';
          parts.push({
            text: `${itemIdx + 1}. ${entry.name}${typeSuffix}: ${value}${share}`,
            fieldKey: `block-${blockIdx}-rank-${itemIdx}`,
            partKind: 'data-field'
          });
          entry.byTask?.forEach((task, taskIdx) => {
            const taskTypeSuffix = task.type ? `, ${task.type}` : '';
            parts.push({
              text: `${task.item}${taskTypeSuffix}: ${formatHours(task.hours)}`,
              fieldKey: `block-${blockIdx}-rank-${itemIdx}-task-${taskIdx}`,
              partKind: 'data-field'
            });
          });
        });
        return;
      }

      if (block.type === 'key_values') {
        if (block.title?.trim()) {
          parts.push({
            text: block.title.trim(),
            fieldKey: `block-${blockIdx}-title`,
            partKind: 'data-field'
          });
        }
        block.rows.forEach((row, rowIdx) => {
          parts.push({
            text: `${formatKeyValueLabel(row.label)}: ${row.value}`,
            fieldKey: `block-${blockIdx}-kv-${rowIdx}`,
            partKind: 'data-field'
          });
        });
        return;
      }

      if (block.type === 'table') {
        if (block.title?.trim()) {
          parts.push({
            text: block.title.trim(),
            fieldKey: `block-${blockIdx}-title`,
            partKind: 'data-field'
          });
        }
        block.rows.forEach((row, rowIdx) => {
          const cells = block.columns
            .map((col) => {
              const value = row[col.prop]?.trim();
              return value ? `${col.fieldName}: ${value}` : '';
            })
            .filter(Boolean);
          if (cells.length) {
            parts.push({
              text: cells.join(', '),
              fieldKey: `block-${blockIdx}-row-${rowIdx}`,
              partKind: 'data-field'
            });
          }
        });
        return;
      }

      if (block.type === 'section') {
        if (block.heading?.trim()) {
          parts.push({
            text: block.heading.trim(),
            fieldKey: `block-${blockIdx}-heading`,
            partKind: 'data-field'
          });
        }
        const spoken = block.text.replace(/\*\*(.+?)\*\*/g, '$1').trim();
        if (spoken) {
          parts.push({
            text: spoken,
            fieldKey: `block-${blockIdx}-section`,
            partKind: 'data-field'
          });
        }
        return;
      }

      const spoken = block.text.replace(/\*\*(.+?)\*\*/g, '$1').trim();
      if (spoken) {
        parts.push({
          text: spoken,
          fieldKey: `block-${blockIdx}-text`,
          partKind: 'data-field'
        });
      }
    });
    return parts;
  }

  if (content.dataKind === 'projects_over_estimate' && content.projectsOverEstimateContent) {
    const estimate = content.projectsOverEstimateContent;
    if (estimate.count != null) {
      parts.push({
        text: `Count: ${estimate.count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }
    if (estimate.overPercent != null) {
      parts.push({
        text: `Over threshold: ${formatPercentComplete(estimate.overPercent)}`,
        fieldKey: 'overPercent',
        partKind: 'data-field'
      });
    }

    estimate.matches.forEach((project, idx) => {
      const details = [project.projectName];
      if (project.owner) {
        details.push(`owned by ${project.owner}`);
      }
      if (project.estimatedHours != null) {
        details.push(`estimated ${formatHours(project.estimatedHours)}`);
      }
      if (project.actualHours != null) {
        details.push(`actual ${formatHours(project.actualHours)}`);
      }
      if (project.overrunPercent != null) {
        details.push(`overrun ${formatPercentComplete(project.overrunPercent)}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `over-estimate-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'project_list') {
    content.projects?.forEach((project) => {
      const details = [project.projectName];
      if (project.status) {
        details.push(project.status);
      }
      if (project.daysOverdue != null) {
        details.push(formatDaysOverdue(project.daysOverdue));
      }
      if (project.overdueCount != null) {
        details.push(formatOverdueCount(project.overdueCount));
      }
      if (project.dueDate) {
        details.push(`due ${formatDailyHoursDate(project.dueDate)}`);
      }
      if (project.projectedCompletion) {
        details.push(`projected ${formatDailyHoursDate(project.projectedCompletion)}`);
      }
      if (project.percentComplete != null) {
        details.push(`${formatPercentComplete(project.percentComplete)} complete`);
      }
      if (project.owner) {
        details.push(`owned by ${project.owner}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: project.projectId || project.projectName,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'project_health' && content.projectHealthContent) {
    const health = content.projectHealthContent;
    parts.push({
      text: health.projectName,
      fieldKey: 'projectName',
      partKind: 'data-field'
    });
    if (health.status) {
      parts.push({ text: `Status ${health.status}`, fieldKey: 'status', partKind: 'data-field' });
    }
    if (health.owner) {
      parts.push({ text: `owned by ${health.owner}`, fieldKey: 'owner', partKind: 'data-field' });
    }
    if (health.dueDate) {
      parts.push({
        text: `due ${formatDailyHoursDate(health.dueDate)}`,
        fieldKey: 'dueDate',
        partKind: 'data-field'
      });
    }
    if (health.percentComplete != null) {
      parts.push({
        text: `${formatPercentComplete(health.percentComplete)} complete`,
        fieldKey: 'percentComplete',
        partKind: 'data-field'
      });
    }
    if (health.daysRemaining != null && !isCompleteProjectHealth(health)) {
      parts.push({
        text: formatDaysRemaining(health.daysRemaining),
        fieldKey: 'daysRemaining',
        partKind: 'data-field'
      });
    }
    if (health.hours?.totalHours != null) {
      parts.push({
        text: `Total hours ${formatHours(health.hours.totalHours)}`,
        fieldKey: 'totalHours',
        partKind: 'data-field'
      });
    }
    if (health.hours?.loggedHours != null) {
      parts.push({
        text: `Logged hours ${formatHours(health.hours.loggedHours)}`,
        fieldKey: 'loggedHours',
        partKind: 'data-field'
      });
    }
    if (health.hours?.pendingHours != null) {
      parts.push({
        text: `Pending hours ${formatHours(health.hours.pendingHours)}`,
        fieldKey: 'pendingHours',
        partKind: 'data-field'
      });
    }
    return parts;
  }

  if (content.dataKind === 'project_tasks' && content.projectTasksContent) {
    if (content.projectTasksContent.projectName) {
      parts.push({
        text: `Project: ${content.projectTasksContent.projectName}`,
        fieldKey: 'projectName',
        partKind: 'data-field'
      });
    }

    content.projectTasksContent.tasks.forEach((task, idx) => {
      const details = [task.taskName];
      if (task.status) {
        details.push(task.status);
      }
      if (task.due) {
        details.push(`due ${formatDailyHoursDate(task.due)}`);
      }
      if (task.assignees) {
        details.push(`assignees ${task.assignees}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `project-task-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'project_task_breakdown' && content.projectTaskBreakdownContent) {
    const breakdown = content.projectTaskBreakdownContent;
    if (breakdown.projectName) {
      parts.push({
        text: `Project: ${breakdown.projectName}`,
        fieldKey: 'projectName',
        partKind: 'data-field'
      });
    }
    if (breakdown.totalTasks != null) {
      parts.push({
        text: `Total tasks ${breakdown.totalTasks}`,
        fieldKey: 'totalTasks',
        partKind: 'data-field'
      });
    }
    if (breakdown.completedTasks != null) {
      parts.push({
        text: `Completed tasks ${breakdown.completedTasks}`,
        fieldKey: 'completedTasks',
        partKind: 'data-field'
      });
    }
    if (breakdown.remainingTasks != null) {
      parts.push({
        text: `Remaining tasks ${breakdown.remainingTasks}`,
        fieldKey: 'remainingTasks',
        partKind: 'data-field'
      });
    }

    breakdown.remaining.forEach((task, idx) => {
      const details = [task.taskName];
      if (task.status) {
        details.push(task.status);
      }
      if (task.percentComplete != null) {
        details.push(`${formatPercentComplete(task.percentComplete)} complete`);
      }
      if (task.assignees) {
        details.push(`assignees ${task.assignees}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `remaining-task-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'completed_items' && content.completedItemsContent) {
    const completed = content.completedItemsContent;
    if (completed.team) {
      parts.push({
        text: `Team: ${completed.team}`,
        fieldKey: 'team',
        partKind: 'data-field'
      });
    }
    if (completed.totalCompleted != null) {
      parts.push({
        text: `Total completed: ${completed.totalCompleted}`,
        fieldKey: 'totalCompleted',
        partKind: 'data-field'
      });
    }

    completed.groups.forEach((group) => {
      group.items.forEach((item, idx) => {
        const details = [item.name];
        if (item.status) {
          details.push(item.status);
        }
        if (item.completedOn) {
          details.push(`completed ${formatDailyHoursDate(item.completedOn)}`);
        }
        if (item.assignees) {
          details.push(`assignees ${item.assignees}`);
        }
        parts.push({
          text: details.join(', '),
          fieldKey: `completed-${group.type}-${idx}`,
          partKind: 'data-field'
        });
      });
    });
    return parts;
  }

  if (content.dataKind === 'current_work' && content.currentWorkContent) {
    const currentWork = content.currentWorkContent;
    currentWork.items.forEach((item, idx) => {
      const details = [item.name];
      if (item.type) {
        details.push(formatDataLabel(item.type));
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `current-work-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'overdue_tasks' && content.overdueTasksContent) {
    if (content.overdueTasksContent.scopeLabel) {
      parts.push({
        text: `Scope: ${content.overdueTasksContent.scopeLabel}`,
        fieldKey: 'scopeLabel',
        partKind: 'data-field'
      });
    }
    if (content.overdueTasksContent.count != null) {
      parts.push({
        text: `Count: ${content.overdueTasksContent.count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }

    content.overdueTasksContent.tasks.forEach((task, idx) => {
      const details = [task.taskName];
      if (task.status) {
        details.push(task.status);
      }
      if (task.daysOverdue != null) {
        details.push(formatDaysOverdue(task.daysOverdue));
      }
      if (task.dueDate) {
        details.push(`due ${formatDailyHoursDate(task.dueDate)}`);
      }
      if (task.assignees) {
        details.push(`assignees ${task.assignees}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `overdue-task-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'overdue_reqs_issues' && content.overdueReqsIssuesContent) {
    const payload = content.overdueReqsIssuesContent;
    if (payload.itemType) {
      parts.push({
        text: `Type: ${formatDataLabel(payload.itemType)}`,
        fieldKey: 'itemType',
        partKind: 'data-field'
      });
    }
    if (payload.scopeLabel) {
      parts.push({
        text: `Scope: ${payload.scopeLabel}`,
        fieldKey: 'scopeLabel',
        partKind: 'data-field'
      });
    }
    if (payload.count != null) {
      parts.push({
        text: `Count: ${payload.count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }

    payload.items.forEach((item, idx) => {
      const details = [item.name];
      if (item.status) {
        details.push(item.status);
      }
      if (item.daysOverdue != null) {
        details.push(formatDaysOverdue(item.daysOverdue));
      }
      if (item.dueDate) {
        details.push(`due ${formatDailyHoursDate(item.dueDate)}`);
      }
      if (item.project) {
        details.push(`project ${item.project}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `overdue-req-issue-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'priority_tasks' && content.priorityTasksContent) {
    if (content.priorityTasksContent.scopeLabel) {
      parts.push({
        text: `Scope: ${content.priorityTasksContent.scopeLabel}`,
        fieldKey: 'scopeLabel',
        partKind: 'data-field'
      });
    }
    if (content.priorityTasksContent.count != null) {
      parts.push({
        text: `Count: ${content.priorityTasksContent.count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }

    content.priorityTasksContent.tasks.forEach((task, idx) => {
      const details = [task.name];
      if (task.status) {
        details.push(task.status);
      }
      if (task.dueDate) {
        details.push(`due ${formatDailyHoursDate(task.dueDate)}`);
      }
      if (task.project) {
        details.push(`project ${task.project}`);
      }
      if (task.assignees) {
        details.push(`assignees ${task.assignees}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `priority-task-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'my_pending_service_tasks' && content.pendingServiceTasksContent) {
    const payload = content.pendingServiceTasksContent;
    if (payload.user) {
      parts.push({
        text: `User: ${payload.user}`,
        fieldKey: 'user',
        partKind: 'data-field'
      });
    }
    if (payload.serviceGroup) {
      parts.push({
        text: `Service group: ${payload.serviceGroup}`,
        fieldKey: 'serviceGroup',
        partKind: 'data-field'
      });
    }
    if (payload.count != null) {
      parts.push({
        text: `Count: ${payload.count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }
    if (payload.shown != null) {
      parts.push({
        text: `Showing: ${payload.shown}`,
        fieldKey: 'shown',
        partKind: 'data-field'
      });
    }

    payload.tasks.forEach((task, idx) => {
      const details = [task.taskName];
      if (task.status) {
        details.push(task.status);
      }
      if (task.dueDate) {
        details.push(`due ${formatDailyHoursDate(task.dueDate)}`);
      }
      if (task.projectName) {
        details.push(`project ${task.projectName}`);
      }
      if (task.serviceName) {
        details.push(`service ${task.serviceName}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `pending-service-task-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'items_due' && content.itemsDueContent) {
    if (content.itemsDueContent.count != null) {
      parts.push({
        text: `Count: ${content.itemsDueContent.count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }

    content.itemsDueContent.items.forEach((item, idx) => {
      const details = [item.name];
      if (item.type) {
        details.push(formatDataLabel(item.type));
      }
      if (item.status) {
        details.push(item.status);
      }
      if (item.dueDate) {
        details.push(`due ${formatDailyHoursDate(item.dueDate)}`);
      }
      if (item.project) {
        details.push(`project ${item.project}`);
      }
      if (item.assignees) {
        details.push(`assignees ${item.assignees}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `items-due-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'work_today' && content.workTodayContent) {
    const appendWorkTodaySpeech = (
      sectionKey: string,
      label: string,
      items: Array<{
        name: string;
        type?: string;
        status?: string;
        dueDate?: string;
        project?: string;
        daysOverdue?: number;
      }>
    ): void => {
      if (!items.length) {
        return;
      }
      parts.push({
        text: `${label}:`,
        fieldKey: `work-today-${sectionKey}`,
        partKind: 'data-field'
      });
      items.forEach((item, idx) => {
        const details = [item.name];
        if (item.type) {
          details.push(formatDataLabel(item.type));
        }
        if (item.status) {
          details.push(item.status);
        }
        if (item.dueDate) {
          details.push(`due ${formatDailyHoursDate(item.dueDate)}`);
        }
        if (item.project) {
          details.push(`project ${item.project}`);
        }
        if (item.daysOverdue != null) {
          details.push(formatDaysOverdue(item.daysOverdue));
        }
        parts.push({
          text: details.join(', '),
          fieldKey: `work-today-${sectionKey}-${idx}`,
          partKind: 'data-field'
        });
      });
    };

    appendWorkTodaySpeech('do-now', 'Do now', content.workTodayContent.doNow);
    appendWorkTodaySpeech('coming-up', 'Coming up', content.workTodayContent.comingUp);
    return parts;
  }

  if (content.dataKind === 'updates' && content.updatesContent) {
    const user = content.updatesContent.user;
    if (user) {
      parts.push({ text: `User: ${user}`, fieldKey: 'user', partKind: 'data-field' });
    }
    const count = content.updatesContent.count;
    if (count != null) {
      parts.push({
        text: `Updates: ${count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }

    const items = content.updatesContent.updates ?? [];
    items.forEach((u, idx) => {
      const type = u.type ? ` ${u.type}` : '';
      const on = u.on ? ` ${u.on}` : '';
      const date = u.date ? ` on ${u.date}` : '';
      const text = u.text ? `: ${u.text}` : '';
      parts.push({
        text: `Update ${idx + 1}${type}${on}${date}${text}`.trim(),
        fieldKey: `updates-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'comment_search' && content.commentSearchContent) {
    const search = content.commentSearchContent;
    if (search.keyword) {
      parts.push({
        text: `Keyword: ${search.keyword}`,
        fieldKey: 'keyword',
        partKind: 'data-field'
      });
    }
    if (search.total != null) {
      parts.push({
        text: `Total: ${search.total}`,
        fieldKey: 'total',
        partKind: 'data-field'
      });
    }

    search.matches.forEach((match, idx) => {
      const title = match.on || match.type || 'Comment';
      const project = match.project ? ` in ${match.project}` : '';
      const by = match.by ? ` by ${match.by}` : '';
      const date = match.date ? ` on ${match.date}` : '';
      const snippet = match.snippet ? `: ${match.snippet}` : '';
      parts.push({
        text: `Comment ${idx + 1}: ${title}${project}${by}${date}${snippet}`,
        fieldKey: `comment-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'my_approvals' && content.myApprovalsContent) {
    const approvals = content.myApprovalsContent;
    if (approvals.total != null) {
      parts.push({
        text: `Total: ${approvals.total}`,
        fieldKey: 'total',
        partKind: 'data-field'
      });
    }
    if (approvals.shown != null) {
      parts.push({
        text: `Showing: ${approvals.shown}`,
        fieldKey: 'shown',
        partKind: 'data-field'
      });
    }

    approvals.approvals.forEach((item, idx) => {
      const details = [item.title];
      if (item.type) {
        details.push(formatDataLabel(item.type));
      }
      if (item.project) {
        details.push(`project ${item.project}`);
      }
      if (item.requestedOn) {
        details.push(`requested ${formatDailyHoursDate(item.requestedOn)}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `approval-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'client_requests' && content.clientRequestsContent) {
    const payload = content.clientRequestsContent;
    if (payload.client) {
      parts.push({
        text: `Client: ${payload.client}`,
        fieldKey: 'client',
        partKind: 'data-field'
      });
    }
    if (payload.count != null) {
      parts.push({
        text: `Count: ${payload.count}`,
        fieldKey: 'count',
        partKind: 'data-field'
      });
    }

    payload.requests.forEach((item, idx) => {
      const details = [item.request];
      if (item.status) {
        details.push(item.status);
      }
      if (item.project) {
        details.push(`project ${item.project}`);
      }
      if (item.dueDate) {
        details.push(`due ${formatDailyHoursDate(item.dueDate)}`);
      }
      parts.push({
        text: details.join(', '),
        fieldKey: `client-request-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'ranking' && content.rankingContent) {
    if (content.rankingContent.ranking.length === 0) {
      return parts;
    }
    if (content.rankingContent.scope) {
      parts.push({
        text: `Scope: ${content.rankingContent.scope}`,
        fieldKey: 'scope',
        partKind: 'data-field'
      });
    }
    if (content.rankingContent.peopleCounted != null) {
      const countLabel = content.rankingContent.countLabel ?? 'People counted';
      parts.push({
        text: `${countLabel}: ${content.rankingContent.peopleCounted}`,
        fieldKey: 'peopleCounted',
        partKind: 'data-field'
      });
    }

    content.rankingContent.ranking.forEach((entry, idx) => {
      const value = formatRankingEntryValue(entry, content.rankingContent?.itemLabel);
      const share = entry.percent != null ? `, ${formatPercentComplete(entry.percent)}` : '';
      parts.push({
        text: `${idx + 1}. ${entry.name}, ${value}${share}`,
        fieldKey: `ranking-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'user' && content.userContent) {
    if (content.userContent.user) {
      parts.push({
        text: `User: ${content.userContent.user}`,
        fieldKey: 'user',
        partKind: 'data-field'
      });
    }
    if (content.userContent.totalHours != null) {
      parts.push({
        text: `Total hours: ${formatHours(content.userContent.totalHours)}`,
        fieldKey: 'totalHours',
        partKind: 'data-field'
      });
    }

    content.userContent.byTask.forEach((entry, idx) => {
      const type = entry.type ? ` (${entry.type})` : '';
      parts.push({
        text: `${entry.item}${type}, ${formatHours(entry.hours)}`,
        fieldKey: `byTask-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'daily_hours' && content.dailyHoursContent) {
    if (content.dailyHoursContent.user) {
      parts.push({
        text: `User: ${content.dailyHoursContent.user}`,
        fieldKey: 'user',
        partKind: 'data-field'
      });
    }
    if (content.dailyHoursContent.totalHours != null) {
      parts.push({
        text: `Total hours: ${formatHours(content.dailyHoursContent.totalHours)}`,
        fieldKey: 'totalHours',
        partKind: 'data-field'
      });
    }
    if (content.dailyHoursContent.dayCount != null) {
      parts.push({
        text: `Days: ${content.dailyHoursContent.dayCount}`,
        fieldKey: 'dayCount',
        partKind: 'data-field'
      });
    }

    content.dailyHoursContent.days.forEach((entry, idx) => {
      parts.push({
        text: `${formatDailyHoursDate(entry.date)}, ${formatHours(entry.hours)}`,
        fieldKey: `day-${idx}`,
        partKind: 'data-field'
      });
    });

    return parts;
  }

  if (content.dataKind === 'ai_hours_trend' && content.aiHoursTrendContent) {
    if (content.aiHoursTrendContent.scopeLabel) {
      parts.push({
        text: `Scope: ${content.aiHoursTrendContent.scopeLabel}`,
        fieldKey: 'scopeLabel',
        partKind: 'data-field'
      });
    }
    if (content.aiHoursTrendContent.totalHours != null) {
      parts.push({
        text: `Total hours: ${formatHours(content.aiHoursTrendContent.totalHours)}`,
        fieldKey: 'totalHours',
        partKind: 'data-field'
      });
    }
    if (content.aiHoursTrendContent.weeksCount != null) {
      parts.push({
        text: `Weeks: ${content.aiHoursTrendContent.weeksCount}`,
        fieldKey: 'weeksCount',
        partKind: 'data-field'
      });
    }

    content.aiHoursTrendContent.weeks.forEach((entry, idx) => {
      const label = entry.weekLabel || formatDailyHoursDate(entry.weekStart);
      parts.push({
        text: `${label}, ${formatHours(entry.hours)}`,
        fieldKey: `week-${idx}`,
        partKind: 'data-field'
      });
    });

    return parts;
  }

  if (content.dataKind === 'leave' && content.leaveContent) {
    if (content.leaveContent.people.length === 0) {
      return parts;
    }
    if (content.leaveContent.scope) {
      parts.push({
        text: `Scope: ${content.leaveContent.scope}`,
        fieldKey: 'scope',
        partKind: 'data-field'
      });
    }
    if (content.leaveContent.peopleCounted != null) {
      parts.push({
        text: `People counted: ${content.leaveContent.peopleCounted}`,
        fieldKey: 'peopleCounted',
        partKind: 'data-field'
      });
    }

    content.leaveContent.people.forEach((person, personIdx) => {
      parts.push({
        text: `${person.name}, ${formatHours(person.totalHours)}`,
        fieldKey: `leave-person-${personIdx}`,
        partKind: 'data-field'
      });

      person.days.forEach((day, dayIdx) => {
        const category = day.category ? `, ${day.category}` : '';
        parts.push({
          text: `${formatDailyHoursDate(day.date)}${category}, ${formatHours(day.hours)}`,
          fieldKey: `leave-person-${personIdx}-day-${dayIdx}`,
          partKind: 'data-field'
        });
      });
    });
    return parts;
  }

  if (content.dataKind === 'free_hours' && content.freeHoursContent) {
    const freeHours = content.freeHoursContent;
    if (freeHours.target) {
      const roleSuffix = freeHours.isRole ? ' role' : '';
      parts.push({
        text: `Target: ${freeHours.target}${roleSuffix}`,
        fieldKey: 'target',
        partKind: 'data-field'
      });
    }
    if (freeHours.date) {
      parts.push({
        text: `Date: ${freeHours.date}`,
        fieldKey: 'date',
        partKind: 'data-field'
      });
    }
    if (freeHours.capacityHours != null) {
      parts.push({
        text: `Capacity: ${formatHours(freeHours.capacityHours)}`,
        fieldKey: 'capacityHours',
        partKind: 'data-field'
      });
    }

    freeHours.people.forEach((person, idx) => {
      const overbooked = person.overbooked ? ', overbooked' : '';
      parts.push({
        text: `${person.name}, scheduled ${formatHours(person.scheduledHours)}, free ${formatHours(person.freeHours)}${overbooked}`,
        fieldKey: `free-hours-person-${idx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'timesheet_submission' && content.timesheetSubmissionContent) {
    const submission = content.timesheetSubmissionContent;
    if (submission.team) {
      parts.push({
        text: `Team: ${submission.team}`,
        fieldKey: 'team',
        partKind: 'data-field'
      });
    }
    if (submission.week) {
      parts.push({
        text: `Week: ${formatDailyHoursDate(submission.week.from)} to ${formatDailyHoursDate(submission.week.to)}`,
        fieldKey: 'week',
        partKind: 'data-field'
      });
    }
    if (submission.notSubmittedCount != null) {
      parts.push({
        text: `Not submitted: ${submission.notSubmittedCount}`,
        fieldKey: 'notSubmittedCount',
        partKind: 'data-field'
      });
    }

    submission.notSubmitted.forEach((person, personIdx) => {
      const status = person.status ? `, ${person.status}` : '';
      parts.push({
        text: `${person.name}${status}`,
        fieldKey: `timesheet-not-submitted-${personIdx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  if (content.dataKind === 'client_brief' && content.clientBriefContent) {
    const brief = content.clientBriefContent;
    if (brief.client) {
      parts.push({
        text: `Client: ${brief.client}`,
        fieldKey: 'client',
        partKind: 'data-field'
      });
    }
    if (brief.activeProjectCount != null) {
      parts.push({
        text: `Active projects: ${brief.activeProjectCount}`,
        fieldKey: 'activeProjectCount',
        partKind: 'data-field'
      });
    }
    if (brief.overdueTaskCount != null) {
      parts.push({
        text: `Overdue tasks: ${brief.overdueTaskCount}`,
        fieldKey: 'overdueTaskCount',
        partKind: 'data-field'
      });
    }
    if (brief.hoursThisMonth != null) {
      const month = brief.monthLabel ? ` (${brief.monthLabel})` : '';
      parts.push({
        text: `Hours this month${month}: ${formatHours(brief.hoursThisMonth)}`,
        fieldKey: 'hoursThisMonth',
        partKind: 'data-field'
      });
    }

    brief.projects.forEach((project, projectIdx) => {
      const priority: string[] = [project.projectName];
      if (project.status) {
        priority.push(project.status);
      }
      if (project.percentComplete != null) {
        priority.push(formatPercentComplete(project.percentComplete));
      }
      if (project.waitingOn) {
        priority.push(`Waiting on ${project.waitingOn}`);
      }
      parts.push({
        text: priority.join(', '),
        fieldKey: `client-brief-project-${projectIdx}`,
        partKind: 'data-field'
      });
    });

    brief.recentActivity.forEach((activity, activityIdx) => {
      parts.push({
        text: activity,
        fieldKey: `client-brief-activity-${activityIdx}`,
        partKind: 'data-field'
      });
    });

    brief.talkingPoints.forEach((point, pointIdx) => {
      parts.push({
        text: point,
        fieldKey: `client-brief-point-${pointIdx}`,
        partKind: 'data-field'
      });
    });
    return parts;
  }

  Object.entries(content.data).forEach(([key, value]) => {
    if (!isDisplayableDataKey(key)) {
      return;
    }
    parts.push({
      text: `${formatDataLabel(key)}: ${formatRecordFieldValue(key, value)}`,
      fieldKey: key,
      partKind: 'data-field'
    });
  });

  return parts;
}
